import { useEffect, useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { App as CapApp } from '@capacitor/app'
import { LogOut } from 'lucide-react'
import { useTranslation } from '../i18n/LanguageContext'

export function BackButtonHandler() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isHindi } = useTranslation()
  const [showExitToast, setShowExitToast] = useState(false)
  const lastBackPressRef = useRef<number>(0)
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let removeListener: (() => void) | undefined

    const setupListener = async () => {
      try {
        const handle = await CapApp.addListener('backButton', () => {
          // 1. Give open modals/dialogs a chance to handle the back press first
          const backEvent = new CustomEvent('app:backpress', { cancelable: true })
          const handled = !window.dispatchEvent(backEvent)
          if (handled) {
            return
          }

          // 2. Check for standard backdrop modal elements in the DOM with close buttons
          const openModals = document.querySelectorAll(
            '[data-modal-overlay], [role="dialog"], .fixed.inset-0.z-50'
          )
          if (openModals.length > 0) {
            const lastModal = openModals[openModals.length - 1]
            if (lastModal) {
              const closeBtn = lastModal.querySelector<HTMLElement>(
                'button[aria-label="close"], button[aria-label="Close"], [data-close-modal], button:has(svg.lucide-x)'
              )
              if (closeBtn) {
                closeBtn.click()
                return
              }
            }
          }

          // 3. If user is on a sub-route (e.g. /cart, /p/product, /orders, /checkout, etc.)
          const pathname = window.location.pathname
          const search = window.location.search

          if (pathname !== '/' || search.length > 0) {
            // Check if there is history to step back into
            if (window.history.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0) {
              navigate(-1)
            } else {
              // Direct deep-link or no previous page: navigate back to Home
              navigate('/')
            }
            return
          }

          // 4. User is on the Root Home page ('/') with no open modals
          // Require double back-press within 2 seconds to exit app
          const now = Date.now()
          if (now - lastBackPressRef.current < 2000) {
            setShowExitToast(false)
            CapApp.exitApp()
          } else {
            lastBackPressRef.current = now
            setShowExitToast(true)
            if (toastTimeoutRef.current) {
              clearTimeout(toastTimeoutRef.current)
            }
            toastTimeoutRef.current = setTimeout(() => {
              setShowExitToast(false)
            }, 2000)
          }
        })

        removeListener = () => {
          handle.remove()
        }
      } catch (err) {
        console.warn('Capacitor App backButton listener not initialized:', err)
      }
    }

    setupListener()

    return () => {
      if (removeListener) {
        removeListener()
      }
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current)
      }
    }
  }, [navigate, location])

  if (!showExitToast) return null

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none transition-all duration-200">
      <div className="flex items-center gap-2 rounded-full bg-stone-900/95 px-4 py-2 text-xs font-semibold text-white shadow-2xl backdrop-blur-md border border-stone-800">
        <LogOut className="h-4 w-4 text-orange-400 shrink-0" />
        <span>
          {isHindi
            ? 'बाहर निकलने के लिए दोबारा बैक दबाएँ'
            : 'Press back again to exit'}
        </span>
      </div>
    </div>
  )
}
